import * as Server from '@wix/ambassador-restaurants-v1-fulfillment-method/types';
import { DayOfWeek } from '../../models';
import { getWeekStartByLocale } from 'weekstart';
import { analyzeAvailabilitySlotsAgainstBaseAvailability } from '../../minutes-range/timeUtils';
import * as Client from '../../models';
import { InvalidDayOfWeekError } from '../../errors';
import { EMPTY_WEEKLY_AVAILABILITY } from '../../olo-settings/fulfillment';
const daysOfWeek = Object.values(DayOfWeek);
const DEFAULT_LOCALE = 'en';
export function convertDayOfWeek(day) {
  const serverToClientMap = {
    [Server.DayOfWeek.MON]: Client.DayOfWeek.MONDAY,
    [Server.DayOfWeek.TUE]: Client.DayOfWeek.TUESDAY,
    [Server.DayOfWeek.WED]: Client.DayOfWeek.WEDNESDAY,
    [Server.DayOfWeek.THU]: Client.DayOfWeek.THURSDAY,
    [Server.DayOfWeek.FRI]: Client.DayOfWeek.FRIDAY,
    [Server.DayOfWeek.SAT]: Client.DayOfWeek.SATURDAY,
    [Server.DayOfWeek.SUN]: Client.DayOfWeek.SUNDAY
  };
  const clientToServerMap = {
    [Client.DayOfWeek.MONDAY]: Server.DayOfWeek.MON,
    [Client.DayOfWeek.TUESDAY]: Server.DayOfWeek.TUE,
    [Client.DayOfWeek.WEDNESDAY]: Server.DayOfWeek.WED,
    [Client.DayOfWeek.THURSDAY]: Server.DayOfWeek.THU,
    [Client.DayOfWeek.FRIDAY]: Server.DayOfWeek.FRI,
    [Client.DayOfWeek.SATURDAY]: Server.DayOfWeek.SAT,
    [Client.DayOfWeek.SUNDAY]: Server.DayOfWeek.SUN
  };
  if (Object.values(Client.DayOfWeek).includes(day)) {
    return clientToServerMap[day];
  } else if (Object.values(Server.DayOfWeek).includes(day)) {
    return serverToClientMap[day];
  } else {
    throw new InvalidDayOfWeekError(day);
  }
}
const isValidLocale = locale =>
// https://stackoverflow.com/questions/8758340/is-there-a-regex-to-test-if-a-string-is-for-a-locale
// matches en, en-US, en-001, zh-Hant-TW, En-au (valid locales)
/^[a-z]{2,4}(-[A-Z][a-z]{3})?(-([A-Z]{2}|[0-9]{3}))?$/.test(locale);
export const getWeekDays = locale => {
  if (!isValidLocale(locale)) {
    return daysOfWeek;
  }
  const firstDayOfWeek = getWeekStartByLocale(locale);
  return daysOfWeek.map((_, index) => {
    const dayIndex = (index + firstDayOfWeek) % 7;
    return daysOfWeek[dayIndex];
  });
};
export const toLocalizedRange = function (locale, isNativeMobile) {
  if (isNativeMobile === void 0) {
    isNativeMobile = false;
  }
  const options = {
    timeStyle: 'short',
    ...(isNativeMobile ? {
      hour: 'numeric',
      minute: 'numeric'
    } : {})
  };
  return _ref => {
    let {
      start,
      end
    } = _ref;
    const validatedLocale = isValidLocale(locale) ? locale : DEFAULT_LOCALE;
    const startDate = new Date();
    startDate.setHours(start.hours, start.minutes);
    const endDate = new Date();
    endDate.setHours(end.hours, end.minutes);
    try {
      return `${startDate.toLocaleTimeString(validatedLocale, options)} - ${endDate.toLocaleTimeString(validatedLocale, options)}`;
    } catch (e) {
      console.log('Error while converting to localized range:', e);
      return `${startDate.toLocaleTimeString(DEFAULT_LOCALE, options)} - ${endDate.toLocaleTimeString(DEFAULT_LOCALE, options)}`;
    }
  };
};
const sortDays = (days, locale) => {
  const order = getWeekDays(locale);
  return days.sort((a, b) => order.indexOf(a) - order.indexOf(b));
};
const mergeAdjacentDays = (days, locale) => {
  const allDays = getWeekDays(locale);
  const sortedDays = sortDays(days, locale);
  const ranges = allDays.reduce((acc, day) => {
    if (sortedDays.includes(day)) {
      if (acc.length === 0 || acc[acc.length - 1].end !== allDays[(allDays.indexOf(day) + 7 - 1) % 7]) {
        acc.push({
          start: day,
          end: day
        });
      } else {
        acc[acc.length - 1].end = day;
      }
    }
    return acc;
  }, []);
  return ranges;
};
export const validateWeeklyAvailability = (baseWeeklyAvailability, weeklyAvailability) => {
  return Object.entries(weeklyAvailability).reduce((validated, _ref2) => {
    let [day, dailyAvailability] = _ref2;
    const dayOfWeek = day;
    const slots = dailyAvailability.availabilities;
    let outOfRangeSlotsIndices = [];
    let isBaseWeekdayUnavailable = false;
    if (baseWeeklyAvailability) {
      const baseSlots = baseWeeklyAvailability[dayOfWeek].availabilities;
      isBaseWeekdayUnavailable = baseWeeklyAvailability[dayOfWeek].availabilities.length === 0;
      if (!isBaseWeekdayUnavailable) {
        outOfRangeSlotsIndices = analyzeAvailabilitySlotsAgainstBaseAvailability(baseSlots, slots).outOfRangeSlotsIndices;
      }
    }
    validated[dayOfWeek] = {
      dayOfWeek,
      availabilities: slots.map((slot, index) => ({
        ...slot,
        isValid: !isBaseWeekdayUnavailable && !outOfRangeSlotsIndices.includes(index),
        isFullWeekdayUnavailable: isBaseWeekdayUnavailable
      }))
    };
    return validated;
  }, {});
};
export const groupDaysBySlot = function (availability, locale, isNativeMobile) {
  if (locale === void 0) {
    locale = DEFAULT_LOCALE;
  }
  if (isNativeMobile === void 0) {
    isNativeMobile = false;
  }
  const groupedDaysBySlot = Object.values(availability).filter(entry => entry.availabilities.length > 0).reduce((acc, daily) => {
    const timeKeys = daily.availabilities.map(toLocalizedRange(locale, isNativeMobile));
    return {
      ...acc,
      ...timeKeys.reduce((map, key) => {
        map[key] = map[key] || [];
        map[key].push(daily.dayOfWeek);
        return map;
      }, acc)
    };
  }, {});
  return Object.fromEntries(Object.entries(groupedDaysBySlot).map(_ref3 => {
    let [slotRange, days] = _ref3;
    return [slotRange, mergeAdjacentDays(days, locale)];
  }));
};
const normalizeLanguageCode = languageCode => {
  const languageAndCountry = languageCode == null || languageCode.split == null ? void 0 : languageCode.split('-');
  if ((languageAndCountry == null ? void 0 : languageAndCountry.length) !== 2) {
    return languageCode;
  }
  return `${languageAndCountry[0]}-${languageAndCountry[1].toUpperCase()}`;
};
const getIdenticalSlotsIndex = (daysBySlots, dailySummarySlots) => {
  return daysBySlots.findIndex(_ref4 => {
    let [_days, slots] = _ref4;
    return slots.length === dailySummarySlots.length && slots.every((slot, index) => {
      const {
        timeSlot,
        isValid
      } = dailySummarySlots[index];
      return slot.timeSlot === timeSlot && slot.isValid === isValid;
    });
  });
};

/**
 * Return a summary of the given WeeklyAvailability object time slots, grouped by weekdays/weekday ranges
 * while validating the availability of each slot against the corresponding given base WeeklyAvailability
 * object time slots
 * @param baseAvailability the base WeeklyAvailability object to validate the given WeeklyAvailability against
 * @param availability the WeeklyAvailability object to summarized and validate
 * @param dayNameProvider a callback which receives a DayOfWeek value and should return the desired weekday name for that weekday (e.g. DayOfWeek.WEDNESDAY => 'Wed', etc.)
 * @param locale the locale in which the time slots should be formatted
 * @param isNativeMobile whether the function is called on a native mobile (e.g., react-native) context (as a different js engine is used)
 */
export const getWeeklyAvailabilitySummary = _ref5 => {
  let {
    availability,
    dayNameProvider,
    languageCode = DEFAULT_LOCALE,
    isNativeMobile = false,
    baseAvailability
  } = _ref5;
  const normalizedLanguageCode = normalizeLanguageCode(languageCode);
  const validatedAvailability = validateWeeklyAvailability(baseAvailability, availability);
  const weekdays = getWeekDays(normalizedLanguageCode);
  const daysBySlots = [];
  weekdays.forEach(dayOfWeek => {
    const dailySummarySlots = validatedAvailability[dayOfWeek].availabilities.map(slot => ({
      timeSlot: toLocalizedRange(normalizedLanguageCode, isNativeMobile)(slot),
      isValid: slot.isValid,
      isFullWeekdayUnavailable: slot.isFullWeekdayUnavailable
    }));
    if (dailySummarySlots.length) {
      const identicalSlotsIndex = getIdenticalSlotsIndex(daysBySlots, dailySummarySlots);
      if (identicalSlotsIndex >= 0) {
        daysBySlots[identicalSlotsIndex][0].push(dayOfWeek);
      } else {
        daysBySlots.push([[dayOfWeek], dailySummarySlots]);
      }
    }
  });
  const summary = new Map();
  daysBySlots.forEach(_ref6 => {
    let [groupDays, slots] = _ref6;
    const ranges = mergeAdjacentDays(groupDays, normalizedLanguageCode);
    const groupDescription = ranges.map(range => {
      return range.start === range.end ? dayNameProvider(range.start) : `${dayNameProvider(range.start)} - ${dayNameProvider(range.end)}`;
    }).join(', ');
    summary.set(groupDescription, slots);
  });
  return summary;
};
export const WeeklyAvailabilityConverter = {
  toServerModel: weeklyAvailability => {
    return Object.values(weeklyAvailability || {}).filter(daily => daily.availabilities.length > 0).map(dayAvailability => ({
      dayOfWeek: convertDayOfWeek(dayAvailability.dayOfWeek),
      timeRanges: dayAvailability.availabilities.map(slot => ({
        startTime: {
          hours: slot.start.hours,
          minutes: slot.start.minutes
        },
        endTime: {
          hours: slot.end.hours,
          minutes: slot.end.minutes
        }
      }))
    }));
  },
  fromServerModel: availability => {
    var _availability$availab;
    return {
      ...EMPTY_WEEKLY_AVAILABILITY,
      ...(availability == null || (_availability$availab = availability.availableTimes) == null ? void 0 : _availability$availab.reduce((acc, dayAvailability) => {
        var _dayAvailability$time;
        return {
          ...acc,
          [convertDayOfWeek(dayAvailability.dayOfWeek)]: {
            dayOfWeek: convertDayOfWeek(dayAvailability.dayOfWeek),
            availabilities: (_dayAvailability$time = dayAvailability.timeRanges) == null ? void 0 : _dayAvailability$time.map(range => {
              var _range$startTime, _range$startTime2, _range$endTime, _range$endTime2;
              return {
                start: {
                  hours: ((_range$startTime = range.startTime) == null ? void 0 : _range$startTime.hours) || 0,
                  minutes: ((_range$startTime2 = range.startTime) == null ? void 0 : _range$startTime2.minutes) || 0
                },
                end: {
                  hours: ((_range$endTime = range.endTime) == null ? void 0 : _range$endTime.hours) || 0,
                  minutes: ((_range$endTime2 = range.endTime) == null ? void 0 : _range$endTime2.minutes) || 0
                }
              };
            })
          }
        };
      }, {}))
    };
  }
};
//# sourceMappingURL=availability-converter.js.map